import {
  countryName,
  getShareScreen,
  removeShareLink,
  renewShare,
  setShareChoices,
  type ShareScreen,
} from '@trippath/shared';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { day } from '../../lib/format';
import { Copyable } from './Copyable';

/** What a link shows and asks, as switched. */
type Choice = 'expires' | 'phrase' | 'amounts' | 'files' | 'names' | 'journal';
const CHOICES: Choice[] = ['expires', 'phrase', 'amounts', 'files', 'names', 'journal'];

/** An instant as `19.02 21:40` or `19 Feb 21:40`, here. */
function when(at: string, language: string): string {
  return new Intl.DateTimeFormat(language === 'et' ? 'et-EE' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(at));
}

/**
 * A trip's read-only link (the `ShareLink` mockup): for its owner, the link to copy, how often and
 * when it was opened and until when it works, its switches — it ends, asks a phrase, shows amounts,
 * files and names — a new link, and each opening near which town and on what; for others, since
 * when it is on.
 */
export function ShareLinkPanel({
  trip,
  owner,
  since,
}: {
  trip: string;
  owner: boolean;
  since: string | null;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const ids = useId();
  const [screen, setScreen] = useState<ShareScreen | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!owner) return;
    let live = true;
    getShareScreen(trip).then(
      (said) => {
        if (live) setScreen(said);
      },
      () => {
        if (live) setFailed(true);
      },
    );
    return () => {
      live = false;
    };
  }, [trip, owner]);
  const done = (job: Promise<ShareScreen>) => {
    setFailed(false);
    job.then(setScreen, () => {
      setFailed(true);
    });
  };
  if (!owner || !screen) {
    return (
      <section className="panel" aria-labelledby={`${ids}-share`}>
        <h2 id={`${ids}-share`}>{t('group.readOnly')}</h2>
        <p className="quiet">{t('group.readOnlyLead')}</p>
        {since !== null && (
          <p>{t('group.sharedSince', { day: day(since.slice(0, 10), lang) })}</p>
        )}
        {failed && (
          <p className="form-failed" role="alert">
            {t('trips.failed')}
          </p>
        )}
      </section>
    );
  }
  const flip = (choice: Choice) => {
    done(
      setShareChoices(trip, {
        expires: screen.expires,
        phrase: screen.phrase !== null,
        amounts: screen.amounts,
        files: screen.files,
        names: screen.names,
        journal: screen.journal,
        [choice]: !(choice === 'phrase' ? screen.phrase !== null : screen[choice]),
      }),
    );
  };
  const on = (choice: Choice) =>
    choice === 'phrase' ? screen.phrase !== null : screen[choice];
  const about = (choice: Choice) =>
    choice === 'expires'
      ? t('share.about.expires', { when: when(screen.endsIfOn, lang) })
      : t(`share.about.${choice}`);
  const said = (outcome: ShareScreen['log'][number]) =>
    outcome.outcome === 'wrong'
      ? t('share.wrong', { count: outcome.tries })
      : t(`share.outcome.${outcome.outcome}`);
  return (
    <>
      <section className="panel" aria-labelledby={`${ids}-share`}>
        <h2 id={`${ids}-share`}>{t('group.readOnly')}</h2>
        {!screen.on ? (
          <>
            <p className="quiet">{t('group.readOnlyLead')}</p>
            <button
              type="button"
              className="button"
              onClick={() => {
                done(renewShare(trip));
              }}
            >
              {t('group.makeLink')}
            </button>
          </>
        ) : (
          <div className="share-body">
            {screen.link ? (
              <Copyable label={t('group.readOnlyLink')} link={screen.link} />
            ) : (
              <p className="quiet">{t('share.unknown')}</p>
            )}
            <div className="share-stats">
              <div>
                <span>{t('share.views')}</span>
                <b>{t('share.times', { count: screen.views })}</b>
              </div>
              <div>
                <span>{t('share.last')}</span>
                <b>{screen.lastAt ? when(screen.lastAt, lang) : '—'}</b>
              </div>
              <div>
                <span>{t('share.until')}</span>
                <b>
                  {screen.endsAt ? day(screen.endsAt.slice(0, 10), lang) : t('share.noEnd')}
                </b>
              </div>
            </div>
            <div className="share-choices">
              {CHOICES.map((choice) => (
                <div key={choice} className="share-choice">
                  <span>{t(`share.choice.${choice}`)}</span>
                  <button
                    type="button"
                    role="switch"
                    className="share-switch"
                    aria-checked={on(choice)}
                    aria-label={t(`share.choice.${choice}`)}
                    onClick={() => {
                      flip(choice);
                    }}
                  />
                  <small>{about(choice)}</small>
                  {choice === 'phrase' && screen.phrase && (
                    <span className="share-phrase" aria-label={t('share.phraseLabel')}>
                      {screen.phrase}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="share-actions">
              <button
                type="button"
                className="button button-danger"
                onClick={() => {
                  setFailed(false);
                  removeShareLink(trip).then(
                    () => {
                      setScreen({ ...screen, on: false, link: null });
                    },
                    () => {
                      setFailed(true);
                    },
                  );
                }}
              >
                {t('share.stop')}
              </button>
              <button
                type="button"
                className="button"
                onClick={() => {
                  done(renewShare(trip));
                }}
              >
                {t('share.renew')}
              </button>
            </div>
            <p className="small quiet">{t('share.renewNote')}</p>
          </div>
        )}
        {failed && (
          <p className="form-failed" role="alert">
            {t('trips.failed')}
          </p>
        )}
      </section>
      {screen.on && (
        <section className="panel" aria-labelledby={`${ids}-views`}>
          <h2 id={`${ids}-views`}>{t('share.log')}</h2>
          {screen.log.length === 0 ? (
            <p className="quiet">{t('share.none')}</p>
          ) : (
            <ul className="share-log">
              {screen.log.map((view) => (
                <li key={`${view.at}-${view.outcome}`}>
                  <span className="mono">{when(view.at, lang)}</span>
                  <span>
                    {[
                      view.city,
                      view.country ? countryName(view.country, lang) : null,
                      t(`share.device.${view.device}`),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    {' · '}
                    <span className={view.outcome === 'wrong' ? 'share-wrong' : undefined}>
                      {said(view)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="small quiet">{t('share.logNote')}</p>
        </section>
      )}
    </>
  );
}
